'use client'

import { useState } from 'react'

import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

interface EmailConfirmResponse {
  status: string
  password_reset_token?: string
}

interface ErrorResponse {
  errors?: string[]
  message?: string
}

export const useEmailConfirm = (): {
  loading: boolean
  error: string | null
  confirmCode: (email: string, code: string) => Promise<string | null>
} => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirmCode = async (email: string, code: string): Promise<string | null> => {
    setError(null)
    setLoading(true)
    try {
      const url = getV5ApiUrl('/users/mobile_verify/email_confirm')
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-brand-id': brand.key,
        },
        body: JSON.stringify({ email, code, brand_id: brand.key }),
      })

      const data = await response.json()

      if (!response.ok) {
        const errorData = data as ErrorResponse
        throw new Error(
          errorData.message || errorData.errors?.[0] || 'Failed to verify code. Please try again.',
        )
      }

      const result = data as EmailConfirmResponse

      if (result.status === 'code_approved') {
        return result.password_reset_token ?? null
      } else if (result.status === 'code_expired') {
        setError('Verification code has expired. Please request a new one.')
        return null
      } else {
        setError('Incorrect verification code. Please try again.')
        return null
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code. Please try again.')
      return null
    } finally {
      setLoading(false)
    }
  }

  return { loading, error, confirmCode }
}
